import React, { useEffect, useMemo, useRef, useState } from "react";

import config from "../../config";
import { WEB_TITLE } from "../../constants/config_ui";
import {
  getFirebaseAuth,
  getFirestore,
} from "../../service/firebase-service";

const CAMPAIGN_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const MAPPING_KEY_SEPARATOR = "::";
const CAMPAIGN_HOSTS = new Set([
  "theunicornclub.org",
  "www.theunicornclub.org",
]);
const CAMPAIGN_PATHS = new Set(["/", "/account-signin"]);
const EVENT_TYPES = [
  {
    key: "landing_viewed",
    label: "Landing page loaded",
    shortLabel: "Page loaded",
  },
  {
    key: "cta_clicked",
    label: "CTA clicked",
    shortLabel: "Clicked",
  },
  {
    key: "form_submitted",
    label: "Form submitted",
    shortLabel: "Submitted",
  },
  {
    key: "lesson_completed",
    label: "Lesson completed",
    shortLabel: "Completed",
  },
];

function parseCsv(text) {
  const rows = [];
  let row = [];
  let value = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (inQuotes) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          value += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        value += character;
      }
    } else if (character === '"') {
      inQuotes = true;
    } else if (character === ",") {
      row.push(value);
      value = "";
    } else if (character === "\n") {
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
    } else if (character !== "\r") {
      value += character;
    }
  }

  if (inQuotes) {
    throw new Error("The CSV contains an unclosed quoted value.");
  }

  if (value || row.length) {
    row.push(value);
    rows.push(row);
  }

  return rows.filter((csvRow) =>
    csvRow.some((cell) => String(cell).trim() !== "")
  );
}

function getCampaignLinkContext(value) {
  if (!value) return null;

  try {
    const url = new URL(value);
    const isProductionCampaignUrl =
      url.protocol === "https:" && CAMPAIGN_HOSTS.has(url.hostname);
    const isLocalDevelopmentUrl =
      process.env.NODE_ENV !== "production" &&
      url.protocol === "http:" &&
      ["localhost", "127.0.0.1"].includes(url.hostname);

    if (
      (!isProductionCampaignUrl && !isLocalDevelopmentUrl) ||
      !CAMPAIGN_PATHS.has(url.pathname)
    ) {
      return null;
    }

    return {
      campaignId: url.searchParams.get("c") || "",
      participantToken: url.searchParams.get("t") || "",
    };
  } catch (error) {
    return null;
  }
}

function getMappingKey(campaignId, token) {
  return `${campaignId}${MAPPING_KEY_SEPARATOR}${token}`;
}

function isValidCampaignId(value) {
  return (
    value.length >= 1 &&
    value.length <= 80 &&
    CAMPAIGN_ID_PATTERN.test(value)
  );
}

function isValidParticipantToken(value) {
  return (
    value.length >= 8 &&
    value.length <= 128 &&
    CAMPAIGN_ID_PATTERN.test(value)
  );
}

function buildRecipientMap(text, expectedCampaignId) {
  const rows = parseCsv(text);

  if (rows.length < 2) {
    throw new Error("The CSV does not contain any participant rows.");
  }

  const headers = rows[0].map((header, index) => {
    const normalized = String(header).trim().toLowerCase();
    return index === 0 ? normalized.replace(/^\uFEFF/, "") : normalized;
  });
  const tokenIndex = headers.indexOf("participant_token");
  const recipientIndex =
    headers.indexOf("recipient") >= 0
      ? headers.indexOf("recipient")
      : headers.indexOf("email");
  const urlIndex = headers.indexOf("url");

  if (tokenIndex < 0 || recipientIndex < 0 || urlIndex < 0) {
    throw new Error(
      'The CSV must contain "participant_token", "recipient", and "url" columns.'
    );
  }

  const recipientByCampaignToken = {};
  let duplicateCount = 0;
  let skippedCount = 0;

  rows.slice(1).forEach((csvRow) => {
    const token = String(csvRow[tokenIndex] || "").trim();
    const recipient = String(csvRow[recipientIndex] || "").trim();
    const linkContext = getCampaignLinkContext(
      String(csvRow[urlIndex] || "").trim()
    );

    if (
      !isValidParticipantToken(token) ||
      !recipient ||
      recipient.length > 254 ||
      !linkContext ||
      !isValidCampaignId(linkContext.campaignId) ||
      linkContext.campaignId !== expectedCampaignId ||
      linkContext.participantToken !== token
    ) {
      skippedCount += 1;
      return;
    }

    const mappingKey = getMappingKey(linkContext.campaignId, token);

    if (recipientByCampaignToken[mappingKey]) {
      duplicateCount += 1;
    }

    recipientByCampaignToken[mappingKey] = recipient;
  });

  if (Object.keys(recipientByCampaignToken).length === 0) {
    throw new Error("The CSV does not contain any valid participant mappings.");
  }

  return { recipientByCampaignToken, duplicateCount, skippedCount };
}

function timestampToDate(timestamp) {
  if (!timestamp) return null;

  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }

  if (typeof timestamp.seconds === "number") {
    return new Date(timestamp.seconds * 1000);
  }

  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTimestamp(timestamp) {
  const date = timestampToDate(timestamp);

  if (!date) return "Time pending";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getTimestampMillis(timestamp) {
  const date = timestampToDate(timestamp);
  return date ? date.getTime() : Number.MIN_SAFE_INTEGER;
}

function getFriendlyAuthError(error) {
  if (
    error &&
    [
      "auth/user-not-found",
      "auth/wrong-password",
      "auth/invalid-email",
      "auth/invalid-credential",
    ].includes(error.code)
  ) {
    return "The email address or password is incorrect.";
  }

  if (error && error.code === "auth/too-many-requests") {
    return "Sign-in is temporarily locked after too many attempts. Try again later.";
  }

  if (error && error.code === "auth/network-request-failed") {
    return "The sign-in service could not be reached. Check your connection and try again.";
  }

  return "Unable to sign in. Check the Firebase Authentication configuration and try again.";
}

function buildParticipantRows(
  events,
  recipientByCampaignToken,
  activeCampaignId
) {
  const participants = new Map();
  const campaignPrefix = `${activeCampaignId}${MAPPING_KEY_SEPARATOR}`;

  Object.entries(recipientByCampaignToken).forEach(
    ([mappingKey, recipient]) => {
      if (!mappingKey.startsWith(campaignPrefix)) return;

      const token = mappingKey.slice(campaignPrefix.length);

      participants.set(token, {
        token,
        recipient,
        isMapped: true,
        events: {},
      });
    }
  );

  events.forEach((event) => {
    const token = event.participant_token;

    if (!token || !isValidParticipantToken(token)) return;

    if (!participants.has(token)) {
      participants.set(token, {
        token,
        recipient: "",
        isMapped: false,
        events: {},
      });
    }

    const participant = participants.get(token);
    const existingTimestamp = participant.events[event.event_type];
    const candidateTimestamp = event.occurred_at;

    if (
      EVENT_TYPES.some((eventType) => eventType.key === event.event_type) &&
      (!existingTimestamp ||
        getTimestampMillis(candidateTimestamp) >
          getTimestampMillis(existingTimestamp))
    ) {
      participant.events[event.event_type] = candidateTimestamp;
    }
  });

  return Array.from(participants.values()).sort((first, second) => {
    const firstLabel = first.recipient || first.token;
    const secondLabel = second.recipient || second.token;
    return firstLabel.localeCompare(secondLabel);
  });
}

function EventStatus({ label, timestamp }) {
  if (!timestamp) {
    return (
      <span className="admin-event-status is-empty" aria-label={`${label}: no`}>
        <span aria-hidden="true">—</span>
        <span>Not recorded</span>
      </span>
    );
  }

  const date = timestampToDate(timestamp);

  return (
    <span className="admin-event-status is-recorded" aria-label={`${label}: yes`}>
      <span className="admin-event-check" aria-hidden="true">
        ✓
      </span>
      <span>
        <span className="admin-event-time-label">Last interaction</span>
        {date ? (
          <time dateTime={date.toISOString()}>
            {formatTimestamp(timestamp)}
          </time>
        ) : (
          <span>{formatTimestamp(timestamp)}</span>
        )}
      </span>
    </span>
  );
}

export default function AdminDashboard() {
  const [auth, setAuth] = useState(null);
  const [database, setDatabase] = useState(null);
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [configurationError, setConfigurationError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState("");
  const [campaignId, setCampaignId] = useState(
    config.tracking.campaignId || ""
  );
  const [activeCampaignId, setActiveCampaignId] = useState("");
  const [events, setEvents] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [
    recipientByCampaignToken,
    setRecipientByCampaignToken,
  ] = useState({});
  const [csvDetails, setCsvDetails] = useState(null);
  const [csvError, setCsvError] = useState("");
  const [csvInputKey, setCsvInputKey] = useState(0);
  const activeCampaignRef = useRef("");

  useEffect(() => {
    window.document.title = `Campaign dashboard | ${WEB_TITLE}`;

    let firebaseAuth;
    let firestore;

    try {
      firebaseAuth = getFirebaseAuth();
      firestore = getFirestore();
    } catch (error) {
      setConfigurationError(
        "Firebase could not be initialized. Check the deployed Firebase configuration."
      );
      setCheckingAuth(false);
      return undefined;
    }

    if (!firebaseAuth || !firestore) {
      setConfigurationError(
        "Firebase is not configured for this deployment. Add the Firebase client settings and rebuild the site."
      );
      setCheckingAuth(false);
      return undefined;
    }

    let cancelled = false;
    let unsubscribeAuth = null;

    setAuth(firebaseAuth);
    setDatabase(firestore);

    firebaseAuth
      .setPersistence("session")
      .then(() => {
        if (cancelled) return;

        unsubscribeAuth = firebaseAuth.onAuthStateChanged(
          (nextUser) => {
            setUser(nextUser);
            setCheckingAuth(false);

            if (!nextUser) {
              activeCampaignRef.current = "";
              setActiveCampaignId("");
              setEvents([]);
              setLastUpdatedAt(null);
              setRecipientByCampaignToken({});
              setCsvDetails(null);
              setCsvError("");
              setCsvInputKey((currentKey) => currentKey + 1);
            }
          },
          () => {
            setConfigurationError(
              "The administrator session could not be checked. Refresh and try again."
            );
            setCheckingAuth(false);
          }
        );
      })
      .catch(() => {
        if (cancelled) return;

        setConfigurationError(
          "A session-only administrator login could not be configured. Check browser storage settings and try again."
        );
        setCheckingAuth(false);
      });

    return () => {
      cancelled = true;
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, []);

  useEffect(() => {
    if (!user || !database || !activeCampaignId) return undefined;

    setLoadingResults(true);
    setResultsError("");

    const query = database
      .collection(config.tracking.collection || "simulation-events")
      .where("campaign_id", "==", activeCampaignId);

    return query.onSnapshot(
      (snapshot) => {
        const nextEvents = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        setEvents(nextEvents);
        setLastUpdatedAt(new Date());
        setLoadingResults(false);
      },
      (error) => {
        setResultsError(
          error && error.code === "permission-denied"
            ? "Access was denied. Confirm this account is authorized by the Firestore rules."
            : "Results could not be loaded. Check Firestore and try again."
        );
        setLoadingResults(false);
      }
    );
  }, [activeCampaignId, database, user]);

  const participantRows = useMemo(
    () =>
      buildParticipantRows(
        events,
        recipientByCampaignToken,
        activeCampaignId
      ),
    [activeCampaignId, events, recipientByCampaignToken]
  );

  const summary = useMemo(() => {
    const mappedParticipants = participantRows.filter(
      (participant) => participant.isMapped
    );
    const reportableParticipants =
      mappedParticipants.length > 0 ? mappedParticipants : participantRows;

    return EVENT_TYPES.reduce((counts, eventType) => {
      return {
        ...counts,
        [eventType.key]: reportableParticipants.filter(
          (participant) => participant.events[eventType.key]
        ).length,
      };
    }, {});
  }, [participantRows]);

  const mappedParticipantCount = useMemo(
    () =>
      participantRows.filter((participant) => participant.isMapped).length,
    [participantRows]
  );

  const unmappedParticipantCount = useMemo(
    () =>
      participantRows.filter((participant) => !participant.isMapped).length,
    [participantRows]
  );

  const handleSignIn = async (event) => {
    event.preventDefault();
    setAuthError("");

    if (!auth) {
      setAuthError("Firebase Authentication is not available.");
      return;
    }

    setSigningIn(true);

    try {
      await auth.setPersistence("session");
      await auth.signInWithEmailAndPassword(email.trim(), password);
    } catch (error) {
      setAuthError(getFriendlyAuthError(error));
    } finally {
      setPassword("");
      setSigningIn(false);
    }
  };

  const handleLogout = async () => {
    if (!auth) return;

    setResultsError("");

    try {
      await auth.signOut();
      activeCampaignRef.current = "";
      setRecipientByCampaignToken({});
      setCsvDetails(null);
      setCsvError("");
      setCsvInputKey((currentKey) => currentKey + 1);
    } catch (error) {
      setResultsError("Unable to sign out. Refresh the page and try again.");
    }
  };

  const handleLoadCampaign = (event) => {
    event.preventDefault();
    const normalizedCampaignId = campaignId.trim();

    if (
      !isValidCampaignId(normalizedCampaignId)
    ) {
      setResultsError(
        "Enter a campaign ID containing only letters, numbers, underscores, or hyphens."
      );
      return;
    }

    setResultsError("");
    setEvents([]);
    setLastUpdatedAt(null);

    if (normalizedCampaignId !== activeCampaignId) {
      setRecipientByCampaignToken({});
      setCsvDetails(null);
      setCsvError("");
      setCsvInputKey((currentKey) => currentKey + 1);
    }

    activeCampaignRef.current = normalizedCampaignId;

    if (normalizedCampaignId === activeCampaignId) {
      setActiveCampaignId("");
      window.setTimeout(() => setActiveCampaignId(normalizedCampaignId), 0);
    } else {
      setActiveCampaignId(normalizedCampaignId);
    }
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    const expectedCampaignId = activeCampaignId;
    setCsvError("");

    if (!file) return;

    if (!expectedCampaignId) {
      setCsvError("Load a campaign before selecting its CSV.");
      setCsvInputKey((currentKey) => currentKey + 1);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setCsvError("Choose a CSV smaller than 2 MB.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const {
          recipientByCampaignToken: nextRecipientMap,
          duplicateCount,
          skippedCount,
        } = buildRecipientMap(
          String(reader.result || ""),
          expectedCampaignId
        );

        if (activeCampaignRef.current !== expectedCampaignId) {
          return;
        }

        setRecipientByCampaignToken(nextRecipientMap);
        setCsvDetails({
          fileName: file.name,
          participantCount: Object.keys(nextRecipientMap).length,
          duplicateCount,
          skippedCount,
        });
      } catch (error) {
        setRecipientByCampaignToken({});
        setCsvDetails(null);
        setCsvError(
          error.message ||
            "The CSV could not be read or does not match this campaign."
        );
      }
    };

    reader.onerror = () => {
      if (activeCampaignRef.current !== expectedCampaignId) {
        return;
      }

      setRecipientByCampaignToken({});
      setCsvDetails(null);
      setCsvError("The CSV could not be read.");
    };

    reader.readAsText(file);
  };

  const clearCsv = () => {
    setRecipientByCampaignToken({});
    setCsvDetails(null);
    setCsvError("");
    setCsvInputKey((currentKey) => currentKey + 1);
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-shell">
          <div>
            <span className="admin-product-name">The Unicorn Club</span>
            <span className="admin-product-label">
              Security exercise dashboard
            </span>
          </div>
          {user && (
            <div className="admin-session">
              <span title={user.email}>{user.email}</span>
              <button
                type="button"
                className="admin-button admin-button-secondary"
                onClick={handleLogout}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="admin-shell admin-main">
        {configurationError && (
          <div className="admin-alert admin-alert-error" role="alert">
            {configurationError}
          </div>
        )}

        {checkingAuth && (
          <section className="admin-card admin-state-card" aria-live="polite">
            <div className="admin-spinner" aria-hidden="true" />
            <p>Checking administrator access…</p>
          </section>
        )}

        {!checkingAuth && !configurationError && !user && (
          <section
            className="admin-card admin-login-card"
            aria-labelledby="admin-login-title"
          >
            <span className="admin-eyebrow">AUTHORIZED ACCESS</span>
            <h1 id="admin-login-title">Campaign dashboard</h1>
            <p>
              Sign in with an administrator account configured in Firebase
              Authentication.
            </p>

            <form onSubmit={handleSignIn}>
              <div className="admin-field">
                <label htmlFor="admin-email">Email address</label>
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </div>

              <div className="admin-field">
                <label htmlFor="admin-password">Password</label>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>

              {authError && (
                <div className="admin-alert admin-alert-error" role="alert">
                  {authError}
                </div>
              )}

              <button
                type="submit"
                className="admin-button admin-button-primary admin-login-button"
                disabled={signingIn}
              >
                {signingIn ? "Signing in…" : "Sign in"}
              </button>
            </form>

            <p className="admin-security-note">
              Credentials are sent to Firebase Authentication and are not
              persisted by this site or in Firestore.
            </p>
          </section>
        )}

        {!checkingAuth && !configurationError && user && (
          <>
            <section className="admin-intro" aria-labelledby="dashboard-title">
              <div>
                <span className="admin-eyebrow">CAMPAIGN REPORTING</span>
                <h1 id="dashboard-title">Security exercise results</h1>
                <p>
                  Load an approved campaign, then optionally match participant
                  tokens to recipients using the locally generated CSV.
                </p>
              </div>
              {activeCampaignId && (
                <span className="admin-live-indicator">
                  <span aria-hidden="true" />
                  Live updates
                </span>
              )}
            </section>

            <div className="admin-controls-grid">
              <section
                className="admin-card admin-control-card"
                aria-labelledby="campaign-control-title"
              >
                <h2 id="campaign-control-title">Load a campaign</h2>
                <form
                  className="admin-inline-form"
                  onSubmit={handleLoadCampaign}
                >
                  <div className="admin-field">
                    <label htmlFor="campaign-id">Campaign ID</label>
                    <input
                      id="campaign-id"
                      type="text"
                      value={campaignId}
                      maxLength="80"
                      pattern="[A-Za-z0-9_-]+"
                      onChange={(event) => setCampaignId(event.target.value)}
                      aria-describedby="campaign-id-help"
                      required
                    />
                    <small id="campaign-id-help">
                      Use the exact ID embedded in the campaign links.
                    </small>
                  </div>
                  <button
                    type="submit"
                    className="admin-button admin-button-primary"
                    disabled={loadingResults}
                  >
                    {loadingResults ? "Loading…" : "Load results"}
                  </button>
                </form>
              </section>

              <section
                className="admin-card admin-control-card"
                aria-labelledby="mapping-control-title"
              >
                <h2 id="mapping-control-title">Match recipients locally</h2>
                <div className="admin-field">
                  <label htmlFor="campaign-csv">Campaign links CSV</label>
                  <input
                    key={csvInputKey}
                    id="campaign-csv"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleCsvUpload}
                    aria-describedby="campaign-csv-help"
                  />
                  <small id="campaign-csv-help">
                    The file stays in this browser and is never uploaded.
                  </small>
                </div>
                {csvDetails && (
                  <div className="admin-file-summary" role="status">
                    <div>
                      <strong>{csvDetails.participantCount}</strong> recipients
                      mapped from <span>{csvDetails.fileName}</span>
                      {(csvDetails.duplicateCount > 0 ||
                        csvDetails.skippedCount > 0) && (
                        <small>
                          {csvDetails.duplicateCount} duplicate and{" "}
                          {csvDetails.skippedCount} incomplete rows skipped.
                        </small>
                      )}
                    </div>
                    <button type="button" onClick={clearCsv}>
                      Clear mapping
                    </button>
                  </div>
                )}
                {csvError && (
                  <div className="admin-alert admin-alert-error" role="alert">
                    {csvError}
                  </div>
                )}
              </section>
            </div>

            {resultsError && (
              <div className="admin-alert admin-alert-error" role="alert">
                {resultsError}
              </div>
            )}

            {activeCampaignId && (
              <>
                <section
                  className="admin-summary-section"
                  aria-labelledby="campaign-summary-title"
                >
                  <div className="admin-section-heading">
                    <div>
                      <span className="admin-eyebrow">SUMMARY</span>
                      <h2 id="campaign-summary-title">{activeCampaignId}</h2>
                    </div>
                    <p aria-live="polite">
                      {loadingResults
                        ? "Updating results…"
                        : lastUpdatedAt
                        ? `Updated ${formatTimestamp(lastUpdatedAt)}`
                        : "Waiting for results…"}
                    </p>
                  </div>

                  <div className="admin-metrics">
                    {EVENT_TYPES.map((eventType) => (
                      <article className="admin-metric" key={eventType.key}>
                        <span>{eventType.label}</span>
                        <strong>{summary[eventType.key] || 0}</strong>
                        <small>
                          {mappedParticipantCount > 0
                            ? `of ${mappedParticipantCount} mapped (${Math.round(
                                ((summary[eventType.key] || 0) /
                                  mappedParticipantCount) *
                                  100
                              )}%)`
                            : "unique tokens; upload CSV for a rate"}
                        </small>
                      </article>
                    ))}
                  </div>
                  <p className="admin-metrics-note">
                    “Landing page loaded” is not an email-open metric. Mail
                    security scanners can preload links, so use clicks and
                    submissions as the stronger interaction signals.
                  </p>
                </section>

                <section
                  className="admin-card admin-results-card"
                  aria-labelledby="participant-results-title"
                >
                  <div className="admin-section-heading">
                    <div>
                      <span className="admin-eyebrow">PARTICIPANTS</span>
                      <h2 id="participant-results-title">Event timeline</h2>
                    </div>
                    <span>
                      {participantRows.length} participant
                      {participantRows.length === 1 ? "" : "s"}
                      {unmappedParticipantCount > 0
                        ? ` · ${unmappedParticipantCount} unmapped`
                        : ""}
                    </span>
                  </div>

                  {loadingResults && participantRows.length === 0 ? (
                    <div className="admin-empty-state" aria-live="polite">
                      <div className="admin-spinner" aria-hidden="true" />
                      <p>Loading campaign events…</p>
                    </div>
                  ) : participantRows.length === 0 ? (
                    <div className="admin-empty-state">
                      <h3>No participants found</h3>
                      <p>
                        No events or local recipient mappings are available for
                        this campaign.
                      </p>
                    </div>
                  ) : (
                    <div className="admin-table-scroll">
                      <table className="admin-results-table">
                        <caption className="admin-visually-hidden">
                          Participant activity for campaign {activeCampaignId}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">Recipient</th>
                            <th scope="col">Participant token</th>
                            {EVENT_TYPES.map((eventType) => (
                              <th scope="col" key={eventType.key}>
                                {eventType.shortLabel}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {participantRows.map((participant) => (
                            <tr key={participant.token}>
                              <th scope="row">
                                {participant.recipient || (
                                  <span className="admin-unmapped">
                                    Unmapped recipient
                                  </span>
                                )}
                              </th>
                              <td>
                                <code>{participant.token}</code>
                              </td>
                              {EVENT_TYPES.map((eventType) => (
                                <td key={eventType.key}>
                                  <EventStatus
                                    label={eventType.label}
                                    timestamp={
                                      participant.events[eventType.key]
                                    }
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </>
            )}

            {!activeCampaignId && (
              <section className="admin-card admin-empty-state">
                <h2>Load a campaign to begin</h2>
                <p>
                  Results are requested from Firestore only after an
                  administrator signs in and chooses a campaign.
                </p>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
